import type { CanonicalOrigin } from '@/lib/seo/canonical-origin';
import { getPublicUrl } from '@/lib/seo/canonical-origin';
import type { SeoMetadata } from '@/lib/seo/metadata';
import type { PublicUnitInfo } from '@/hooks/use-public-site';
import type { PublicCalendarEvent, Site } from '@/types/database';

export interface JsonLdOrganization {
  '@context': 'https://schema.org';
  '@type': 'Organization';
  '@id': string;
  name: string;
  url: string;
  logo?: string;
  description?: string;
  address?: string;
}

export interface JsonLdWebSite {
  '@context': 'https://schema.org';
  '@type': 'WebSite';
  '@id': string;
  url: string;
  name: string;
}

export interface JsonLdWebPage {
  '@context': 'https://schema.org';
  '@type': 'WebPage';
  '@id': string;
  url: string;
  name: string;
  description?: string;
  isPartOf: { '@id': string };
}

export interface JsonLdEvent {
  '@context': 'https://schema.org';
  '@type': 'Event';
  '@id': string;
  name: string;
  startDate: string;
  endDate?: string;
  location?: { '@type': 'Place'; name: string };
  organizer: { '@id': string };
}

export type JsonLdDocument = JsonLdOrganization | JsonLdWebSite | JsonLdWebPage | JsonLdEvent;

// Event schema is real and buildable — the calendar block already sources
// structured event rows (get_public_site_calendar_events), so this isn't
// guessing at content the way Article/FAQ would be. Only start/end time and
// a plain-text location name are ever available (no street address, no
// online/offline distinction, no organizer contact) — those fields are
// simply omitted rather than fabricated. Article and FAQ schema remain out
// of scope: there is no dated/authored "article" block or structured
// question/answer block in the current data model (the closest thing, the
// generic `list` block, is just an array of strings with no inherent Q&A
// structure) — mapping either to schema.org types would mean inventing
// structure the unit never actually entered, which is exactly what
// SEO.md's own Section 31 says not to do without confirming a schema
// change first.
export function buildEventStructuredData(events: PublicCalendarEvent[], organizationId: string): JsonLdEvent[] {
  return events.map((event) => ({
    '@context': 'https://schema.org',
    '@type': 'Event',
    '@id': `${organizationId.replace(/#organization$/, '')}#event-${event.event_id}`,
    name: event.title,
    startDate: event.start_at,
    ...(event.end_at ? { endDate: event.end_at } : {}),
    ...(event.location_name ? { location: { '@type': 'Place' as const, name: event.location_name } } : {}),
    organizer: { '@id': organizationId },
  }));
}

// Built only from fields the unit has actually entered (org_profiles/site
// data) — no placeholder address, contact, or founding-date values.
export function buildStructuredData(params: {
  site: Site;
  unit: PublicUnitInfo;
  origin: CanonicalOrigin;
  metadata: SeoMetadata;
}): JsonLdDocument[] {
  const { site, unit, origin, metadata } = params;
  const homeUrl = getPublicUrl(origin);
  const organizationId = `${homeUrl}#organization`;
  const webSiteId = `${homeUrl}#website`;

  const organization: JsonLdOrganization = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': organizationId,
    name: unit.name,
    url: homeUrl,
    ...(site.logo_url ? { logo: site.logo_url } : {}),
    ...(site.seo_description ? { description: site.seo_description } : {}),
    ...(unit.location ? { address: unit.location } : {}),
  };

  const webSite: JsonLdWebSite = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': webSiteId,
    url: homeUrl,
    name: unit.name,
  };

  const webPage: JsonLdWebPage = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${metadata.canonicalUrl}#webpage`,
    url: metadata.canonicalUrl,
    name: metadata.title,
    ...(metadata.description ? { description: metadata.description } : {}),
    isPartOf: { '@id': webSiteId },
  };

  return [organization, webSite, webPage];
}
